/**
 * Frontier guard + shared presentational bits (Sprint 282 Stream G).
 *
 * REQ-FRT-019: the section is hidden without ``Module:frontier#view`` and a
 * direct URL resolves to a representative denial page (no data fetched).
 * ``useModuleAccess`` is fail-closed (SEC-003), so loading/error/no-tenant
 * also deny.
 */

import type { ReactNode } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useModuleAccess } from '@/hooks/useModuleAccess';
import { useI18n } from '@/i18n';

export function FrontierGuard({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  const canView = useModuleAccess('frontier', 'view');

  if (!canView) {
    return (
      <div className="p-6" data-testid="frontier-denied">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-amber-500" aria-hidden="true" />
              {t('frontier.denied.title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('frontier.denied.message')}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }
  return <>{children}</>;
}

const RUN_STATUS_STYLES: Record<string, string> = {
  completed: 'text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-300',
  failed: 'text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300',
  blocked: 'text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-300',
  expired: 'text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-300',
  running: 'text-blue-700 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-300',
  retrying: 'text-blue-700 bg-blue-50 dark:bg-blue-900/20 dark:text-blue-300',
  queued: 'text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
  cancelled: 'text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
  paused: 'text-violet-700 bg-violet-50 dark:bg-violet-900/20 dark:text-violet-300',
  waiting_input: 'text-violet-700 bg-violet-50 dark:bg-violet-900/20 dark:text-violet-300',
  waiting_approval: 'text-violet-700 bg-violet-50 dark:bg-violet-900/20 dark:text-violet-300',
  emergency_stopped: 'text-red-700 bg-red-50 dark:bg-red-900/20 dark:text-red-300',
};

const ROUTINE_STATUS_STYLES: Record<string, string> = {
  active: 'text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-300',
  draft: 'text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
  paused: 'text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-300',
  auto_paused: 'text-amber-700 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-300',
  expired: 'text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
  disabled: 'text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-300',
  archived: 'text-gray-500 bg-gray-100 dark:bg-gray-800 dark:text-gray-400',
};

export function StatusChip({
  kind,
  value,
  label,
}: {
  kind: 'run' | 'routine';
  value: string;
  label: string;
}) {
  const styles = kind === 'run' ? RUN_STATUS_STYLES : ROUTINE_STATUS_STYLES;
  return (
    <span
      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
        styles[value] ?? styles.queued ?? 'bg-gray-100 text-gray-700'
      }`}
      data-status={value}
    >
      {label}
    </span>
  );
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

/** Numeric KPI used by the overview page. */
export function KpiCard({ label, value }: { label: string; value: string | number }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
        <p className="mt-1 text-2xl font-semibold">{value}</p>
      </CardContent>
    </Card>
  );
}
